import spread from '../data/tarot/spread.json'
import { CARD_COUNT, seededPicker, type TarotField, type TarotTextTable } from './tarot'
import type { Direction } from './tarotTopic'
import type { Locale } from './constants'

/**
 * 켈틱 크로스. 타로를 **타로답게** 보는 자리다.
 *
 * 카드 한 장을 뽑아 모으는 것이 아니라, 질문을 하고 열 장을 펼쳐
 * **자리마다 다른 뜻**으로 읽고, 마지막에 전체를 하나로 묶어 답한다.
 *
 * 자리별 풀이는 **미리 쓴 자료를 조합해서** 만든다 (AI 를 부르지 않는다).
 * 카드 78장 × 정·역 × 자리 10개 = 1,560가지라 미리 다 쓸 수 없기 때문에,
 * 자리마다 "이 자리는 이렇게 읽는다" 는 틀을 두고 카드의 기존 뜻과 붙인다.
 * **AI 는 마지막 종합 해석 한 번만** 쓴다 (src/lib/tarotPrompt.ts).
 */

/** 열 자리. 순서가 곧 1~10번이다. */
export const SPREAD_POSITIONS = spread.positions as readonly PositionKey[]
export type PositionKey =
  | 'now'
  | 'cross'
  | 'conscious'
  | 'unconscious'
  | 'past'
  | 'coming'
  | 'self'
  | 'around'
  | 'hope'
  | 'outcome'

export const SPREAD_SIZE = SPREAD_POSITIONS.length

interface PositionText {
  name: string
  read: string
}
const TEXT = spread.text as Record<string, Record<string, PositionText>>

/** 그 언어의 자리 이름과 읽는 법 */
export function positionText(locale: Locale, position: PositionKey): PositionText {
  return TEXT[locale]?.[position] ?? TEXT.en![position]!
}

/** 펼쳐진 카드 한 장 */
export interface SpreadCard {
  position: PositionKey
  id: number
  direction: Direction
}

/**
 * 열 장을 펼친다. **같은 카드가 두 번 나오지 않는다.**
 * 씨앗을 주면 같은 패가 나오고(저장된 결과를 다시 열 때), 주지 않으면 매번 다르다.
 */
export function drawSpread(seed?: string): SpreadCard[] {
  const picked = seed ? pickedFromSeed(seed) : null
  if (picked) {
    // 손님이 엎어 놓은 78장에서 직접 고른 판 — 씨앗이 섞은 차례에서 고른 자리의 카드를 차례로 놓는다
    const deck = shuffledDeck(picked.base)
    return SPREAD_POSITIONS.map((position, i) => ({ position, ...deck[picked.slots[i]!]! }))
  }
  const pick = seededPicker(seed ?? `${Date.now()}|${Math.random()}`)
  const used = new Set<number>()
  const out: SpreadCard[] = []
  for (const position of SPREAD_POSITIONS) {
    let id = pick(CARD_COUNT)
    // 카드가 자리 수보다 적으면 겹칠 수밖에 없다 — 그때는 겹치는 대로 둔다
    for (let guard = 0; used.has(id) && guard < CARD_COUNT * 4; guard += 1) id = pick(CARD_COUNT)
    used.add(id)
    out.push({ position, id, direction: pick(2) === 0 ? 'upright' : 'reversed' })
  }
  return out
}

/**
 * **엎어 놓은 덱** ("전체 카드를 펼쳐 놓고 손님이 직접 고르기").
 * 씨앗으로 78장을 섞고(방향도 함께) 화면에 뒷면으로 펼친다. 손님이 고른 자리 번호 열 개를 씨앗 끝에
 * `|p` + 두 자리 16진수 × 10 으로 붙인다 — 같은 씨앗이면 화면·서버·지난 기록·공유가 같은 판을 다시 만든다.
 * 고른 자리가 없는 옛 씨앗은 예전 그대로 뽑는다.
 */
export const PICK_MARK = '|p'
export function shuffledDeck(base: string): { id: number; direction: Direction }[] {
  const pick = seededPicker(`${base}|deck`)
  const ids = Array.from({ length: CARD_COUNT }, (_, i) => i)
  for (let i = ids.length - 1; i > 0; i -= 1) {
    const j = pick(i + 1)
    ;[ids[i], ids[j]] = [ids[j]!, ids[i]!]
  }
  return ids.map((id) => ({ id, direction: pick(2) === 0 ? 'upright' : 'reversed' }))
}
export function pickedFromSeed(seed: string): { base: string; slots: number[] } | null {
  const at = seed.lastIndexOf(PICK_MARK)
  if (at < 0) return null
  const hex = seed.slice(at + PICK_MARK.length)
  if (!new RegExp(`^[0-9a-f]{${SPREAD_SIZE * 2}}$`).test(hex)) return null
  const slots = Array.from({ length: SPREAD_SIZE }, (_, i) => parseInt(hex.slice(i * 2, i * 2 + 2), 16))
  if (slots.some((s) => s >= CARD_COUNT) || new Set(slots).size !== slots.length) return null
  return { base: seed.slice(0, at), slots }
}
export function seedWithPicks(base: string, slots: number[]): string {
  return `${base}${PICK_MARK}${slots.map((s) => s.toString(16).padStart(2, '0')).join('')}`
}

/** 자리 하나의 풀이 */
export interface SpreadReading {
  position: PositionKey
  /** 자리 이름 ("현재 상황") */
  positionName: string
  /** 그 자리를 어떻게 읽는지 */
  positionRead: string
  cardId: number
  cardName: string
  direction: Direction
  /** 카드의 한 줄 뜻 */
  summary: string
  /** 그 주제에 맞춘 본문 */
  body: string
}

/**
 * 자리 × 카드 를 합쳐 열 자리 풀이를 만든다. **AI 없이** 자료만 쓴다.
 * `field` 는 카드 자료의 어느 칸을 읽을지 정한다 (love_dating·work·business…).
 */
export function readSpread(cards: SpreadCard[], table: TarotTextTable, field: TarotField, locale: Locale): SpreadReading[] {
  return cards.map((card) => {
    const entry = table[String(card.id)]
    if (!entry) throw new Error(`카드 문장이 없다: ${card.id}`)
    const side = entry[card.direction]
    const where = positionText(locale, card.position)
    return {
      position: card.position,
      positionName: where.name,
      positionRead: where.read,
      cardId: card.id,
      cardName: entry.name,
      direction: card.direction,
      summary: side.summary,
      body: side[field],
    }
  })
}
